import styled from '@emotion/styled'
import {theme} from '../../core/theme'
import logoutIcon from '../../core/assets/sideNav/icons/logout-active.svg'

export const HEADER_HEIGHT = 64

// Transparent, fixed bar that only positions the floating pill — the page
// background (AppBackground) shows through everywhere else. `left` clears the
// fixed sidebar (see SIDEBAR_* in App's AppContainer grid: 245px / 64px).
export const HeaderBar = styled.header`
  position: fixed;
  top: 0;
  right: 0;
  left: ${({isSidebarOpen, fullWidth}) =>
    fullWidth ? '0' : isSidebarOpen ? '245px' : '64px'};
  height: ${HEADER_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.space[3]} ${theme.space[6]} 0;
  z-index: 98;
  font-family: ${theme.fonts.body};
  // Transparent over the page background. <main> (the scroller) starts below
  // this bar, so content never slides under it — no scroll state needed.
  pointer-events: none;

  // Opt-in (see AppHeader blurOnScroll): once the page has scrolled, the bar
  // becomes a transparent, blurred strip so content passes softly beneath it.
  transition: background-color 0.2s ease, backdrop-filter 0.2s ease;
  ${({isScrolled}) =>
    isScrolled
      ? `
    background: rgb(255 255 255 / 0%);
    -webkit-backdrop-filter: blur(4px);
    backdrop-filter: blur(4px);
  `
      : ''}

  a {
    pointer-events: auto;
  }

  // Mobile: the bar itself becomes a full-width white header (the floating
  // pill look only makes sense when there's room around it).
  @media (max-width: 835px) {
    left: 0;
    justify-content: space-between;
    padding: 0 ${theme.space[4]};
    background: ${theme.palette.neutral[0]};
    box-shadow: 0 2px 12px rgba(61, 81, 153, 0.12);
    pointer-events: auto;
  }

  @media print {
    display: none !important;
  }
`

// Route title, left-aligned in the bar (outside the pill). The pill gets
// margin-left: auto so it stays right-aligned even when there's no title.
export const HeaderTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${theme.space[1]};
  min-width: 0;
  margin-right: ${theme.space[4]};
  pointer-events: auto;

  @media (max-width: 835px) {
    flex: 1;
  }
`

export const HeaderTitle = styled.h1`
  margin: 0;
  font-size: ${theme.fontSizes['3xl']};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 835px) {
    font-size: ${theme.fontSizes.xl};
  }
`

export const HeaderSubtitle = styled.p`
  margin: 0;
  font-size: ${theme.fontSizes.md};
  color: ${theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 835px) {
    display: none;
  }
`

// Brand logo at the left of the bar (when there is no side nav).
export const HeaderLogo = styled.div`
  pointer-events: auto;
  flex: none;
  display: flex;
  align-items: center;
  margin-right: ${theme.space[4]};

  img {
    display: block;
    width: 93px;
    height: 38px;
  }
`

export const HeaderPill = styled.div`
  position: relative;
  pointer-events: auto;
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: ${theme.space[3]};
  padding: ${({hasActions}) => (hasActions ? '8px 12px' : '8px 18px')};
  background: ${theme.palette.neutral[0]};
  border-radius: ${theme.radii.full};
  box-shadow: 0 2px 12px rgba(61, 81, 153, 0.12);

  @media (max-width: 835px) {
    justify-content: flex-end;
    gap: ${theme.space[2]};
    padding: 0;
    background: transparent;
    border-radius: 0;
    box-shadow: none;
  }
`

// Mobile nav toggle: soft rounded square in the redesign palette, replacing
// the bare Maverick "bars" glyph. Pushed to the far left of the full-width bar.
export const HamburgerButton = styled.button`
  && {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    margin-right: ${theme.space[3]};
    padding: 0;
    border: 1px solid ${theme.palette.thryv.borderAlt};
    border-radius: ${theme.radii.lg2};
    background-color: ${theme.palette.thryv.bg};
    color: ${theme.palette.thryv.blue};
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: ${theme.palette.thryv.bgAlt};
    }

    &:focus-visible {
      outline: none;
      box-shadow: ${theme.shadows.focus};
    }
  }

  svg line {
    transform-box: fill-box;
    transform-origin: center;
    transition: transform 0.2s ease, opacity 0.2s ease;
  }
`

// `&&` doubles the class specificity so global `button { background... }`
// resets (Tailwind preflight in Maverick, reboot) can never win over these.
export const PrimaryAction = styled.button`
  && {
    display: inline-flex;
    align-items: center;
    gap: ${theme.space[2]};
    height: 30px;
    padding: ${({plain}) =>
      plain
        ? `0 ${theme.space[3]}`
        : `0 ${theme.space[3]} 0 ${theme.space[1]}`};
    border: none;
    border-radius: ${theme.radii.full};
    background-color: ${theme.palette.thryv.orange.base};
    background-image: none;
    color: ${theme.palette.neutral[0]};
    font-family: inherit;
    font-size: ${theme.fontSizes.base};
    font-weight: ${theme.fontWeights.medium};
    white-space: nowrap;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background-color: ${theme.palette.thryv.orange.dark};
    }

    &:focus-visible {
      outline: none;
      box-shadow: ${theme.shadows.focus};
    }
  }

  .plus {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${theme.palette.neutral[0]};
    color: ${theme.palette.thryv.orange.base};
    display: flex;
    flex: 0 0 20px;
    align-items: center;
    justify-content: center;
    padding: 0;
    line-height: 0;

    svg {
      display: block;
      flex: none;
      margin: 0;
    }
  }
`

// Figma logout glyph, drawn as a mask so it takes the button's text color.
export const LogoutIcon = styled.span`
  display: block;
  width: 18px;
  height: 18px;
  background-color: currentColor;
  -webkit-mask: url(${logoutIcon}) center / contain no-repeat;
  mask: url(${logoutIcon}) center / contain no-repeat;
`

export const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #8c96c6;
  cursor: pointer;

  &:hover {
    background: ${theme.palette.thryv.bg};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${theme.shadows.focus};
  }
`

export const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.space[3]};
  padding: 0;
  border: none;
  background: transparent;
  font-family: inherit;
  cursor: pointer;
  text-align: right;

  &:focus-visible {
    outline: none;
    box-shadow: ${theme.shadows.focus};
    border-radius: ${theme.radii.full};
  }
`

export const UserGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space[3]};
`

// Header name when it navigates (admin): looks like plain text until hovered.
export const UserNameLink = styled.button`
  && {
    padding: 0;
    border: none;
    background: transparent;
    font-family: inherit;
    font-size: ${theme.fontSizes.base};
    font-weight: ${theme.fontWeights.bold};
    color: ${theme.colors.text.primary};
    text-align: right;
    cursor: pointer;

    &:hover {
      color: ${theme.palette.thryv.cornflower.dark};
      text-decoration: underline;
    }

    &:focus-visible {
      outline: none;
      box-shadow: ${theme.shadows.focus};
    }
  }
`

// Name + role stacked in the user pill; hidden on narrow screens (avatar only).
export const UserInfo = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  line-height: 1.15;
  font-size: ${theme.fontSizes.md};
  color: ${theme.colors.text.primary};

  @media (max-width: 560px) {
    display: none;
  }
`

export const UserFullName = styled.span`
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.bold};
`

export const UserRole = styled.span`
  padding: 1px 8px;
  border: 1px solid ${theme.palette.thryv.blue};
  border-radius: ${theme.radii.full};
  color: ${theme.palette.thryv.blue};
  background: #eef0ff;
  font-size: ${theme.fontSizes.xxs};
  font-weight: ${theme.fontWeights.medium};
`

export const Avatar = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({size}) => size || 36}px;
  height: ${({size}) => size || 36}px;
  border-radius: 50%;
  overflow: hidden;
  // Figma avatar: solid disc with a white ring and bold white initials. On a
  // white surface the ring alone disappears, so a thin halo outside it keeps
  // the circle's edge defined.
  background: ${theme.palette.thryv.orange.base};
  border: 2px solid ${theme.palette.neutral[0]};
  box-shadow: 0 0 0 1px ${theme.palette.thryv.orange.base};
  color: ${theme.palette.neutral[0]};
  font-size: ${theme.fontSizes.base};
  font-weight: ${theme.fontWeights.bold};
  letter-spacing: 0.02em;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

// Same surface the Account Management journey uses for its step cards
// (AccountSetup.styles StepCardWrapper), so Role/Environment read as one family.
const JOURNEY_SURFACE = '#f1f5ff'
// const JOURNEY_SURFACE = 'linear-gradient(180deg, #f1f2ff 88.65%, #fdfdff 100%)'
const JOURNEY_BORDER = '#d6def0'

export const UserMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 100;
  width: 300px;
  padding: ${theme.space[2]};
  display: ${({open}) => (open ? 'flex' : 'none')};
  flex-direction: column;
  gap: ${theme.space[3]};
  background: ${theme.palette.neutral[0]};
  border: 1px solid ${theme.palette.thryv.borderAlt};
  border-radius: ${theme.radii.xl};
  box-shadow: 0 12px 32px rgba(61, 81, 153, 0.18),
    0 2px 6px rgba(61, 81, 153, 0.08);
  font-family: ${theme.fonts.body};
  font-size: ${theme.fontSizes.base};
  color: ${theme.colors.text.primary};
  text-align: left;
`

export const MenuProfile = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space[3]};
  min-width: 0;
  padding: ${theme.space[2]} ${theme.space[2]} ${theme.space[1]};
`

export const MenuProfileText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

export const MenuProfileName = styled.span`
  font-weight: ${theme.fontWeights.bold};
  font-size: ${theme.fontSizes.xl};
`

export const MenuProfileEmail = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const MenuEmailRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space[1.5]};
  min-width: 0;
`

export const CopyButton = styled.button`
  && {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    border-radius: ${theme.radii.sm};
    background-color: transparent;
    color: ${({copied}) => (copied ? theme.palette.success[700] : '#8c96c6')};
    cursor: pointer;

    &:hover {
      background-color: ${theme.palette.thryv.bg};
    }

    &:focus-visible {
      outline: none;
      box-shadow: ${theme.shadows.focus};
    }
  }
`

export const DetailsCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[3]};
  padding: ${theme.space[3]} ${theme.space[4]};
  background: ${JOURNEY_SURFACE};
  border: 1px solid ${JOURNEY_BORDER};
  border-radius: ${theme.radii.xl};
  // border-bottom-right-radius: 20px;
  // border-bottom-left-radius: 20px;
`

export const DetailRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.space[3]};
`

export const DetailLabel = styled.span`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
`

export const DetailValue = styled.span`
  font-size: 12px;
  font-weight: ${theme.fontWeights.regular};
  text-align: right;
`

export const RoleChips = styled.span`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${theme.space[1]};
`

export const RoleChip = styled.span`
  padding: 1px ${theme.space[2]};
  border: 1px solid ${theme.palette.thryv.blue};
  border-radius: ${theme.radii.full};
  background: #eef0ff;
  color: ${theme.palette.thryv.blue};
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.medium};
`
